'use client';
import {useState, useEffect} from 'react';
import {useSession, emailOtp} from '@features/auth/authClient';
import {useMutation} from '@tanstack/react-query';
import styles from '../styles.module.css';
import {Link, useNavigate, useSearch} from '@tanstack/react-router';
import amplitudeService from '@features/analytics/amplitudeService';
import {RightSideImage} from '@features/ui/components/RightSideImage';
import {captureException} from '@sentry/react';
import {BananiAuthError, getErrorMessage} from '../util';
import {LoaderFullScreen} from '@features/ui/components/LoaderFullScreen';
import {BananaSpinner} from '@features/ui/components/BananaSpinner';
import {Button} from '@features/ui/components/Button';
import {ChevronLeft} from 'lucide-react';

export default function Reset() {
  const {isAuthenticated, isPending} = useSession();
  const navigate = useNavigate();
  const {email: emailParam} = useSearch({from: '/auth/reset'});
  const [showOtp, setShowOtp] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [success, setSuccess] = useState(false);

  const [email, setEmail] = useState(emailParam ?? '');

  useEffect(() => {
    amplitudeService.logEvent('[PasswordReset] page rendered');
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      void navigate({to: '/', replace: true});
    }
  }, [navigate, isAuthenticated]);

  const handlePasswordReset = async () => {
    amplitudeService.logEvent('[PasswordReset] send link', {email});

    if (!email) {
      throw new BananiAuthError('Please enter a valid email address');
    }

    if (!showOtp) {
      const res = await emailOtp.requestPasswordReset({email});
      if (res.error) {
        console.error(res.error);
        captureException(res.error);
        throw new Error(res.error.message || res.error.statusText);
      }
      console.log('Password reset email sent to', email);
      amplitudeService.logEvent('[PasswordReset] email sent', {email});

      setShowOtp(true);

      return;
    }

    if (otp && newPassword.length >= 8) {
      const res = await emailOtp.resetPassword({
        email,
        password: newPassword,
        otp,
      });
      if (res.error) {
        console.error(res.error);
        captureException(res.error);
        throw new Error(
          res.error.message ?? res.error.code ?? res.error.statusText,
        );
      }
      amplitudeService.logEvent('[PasswordReset] email sent', {email});

      setSuccess(true);
      setShowOtp(false);
    } else {
      throw new Error('Please enter a valid OTP and password!');
    }
  };

  const resetMutation = useMutation({
    mutationFn: handlePasswordReset,
    onError: (error) => {
      captureException(error);
      amplitudeService.logEvent('[PasswordReset] error sending email', {
        email,
      });
    },
  });

  if (isPending) {
    return <LoaderFullScreen />;
  }

  return (
    <div className={`flex h-screen bg-white`}>
      <title>Banani | Reset Password</title>
      <div className='absolute left-6 top-6'>
        <Link to={'/auth/signin'} search={{email}}>
          <button
            className='flex h-6 w-6 shrink-0 items-center justify-center'
            aria-label='Go back'
          >
            <ChevronLeft className='h-6 w-6 text-gray-600' />
          </button>
        </Link>
      </div>
      <div className='flex w-full items-center justify-center p-8 md:w-1/2'>
        <div className='w-full max-w-80 space-y-8'>
          <div className='text-center'>
            <h2 className='text-[22px] font-semibold text-gray-900'>
              Reset your password
            </h2>
            <p className='mx-auto mt-1 max-w-64 text-sm font-normal text-gray-400'>
              We'll email you a code with which you can reset your password
            </p>
          </div>
          <form className='mt-8 space-y-4'>
            <div className='space-y-4'>
              <div>
                <input
                  id='email-address'
                  name='email'
                  type='email'
                  autoComplete='email'
                  required
                  disabled={showOtp || resetMutation.isPending || success}
                  className={
                    'relative block h-10 w-full rounded-lg px-3 text-sm text-gray-900 placeholder-gray-400 ' +
                    styles.input
                  }
                  placeholder='Email'
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                  }}
                  autoFocus
                />
              </div>
            </div>

            {showOtp && (
              <>
                <div className='text-sm text-black'>
                  To reset your password, we've sent a one-time-password to{' '}
                  {email}
                </div>
                <input
                  id='otp'
                  name='otp'
                  type='text'
                  autoComplete='one-time-code'
                  disabled={success}
                  required
                  className={
                    'relative block h-10 w-full appearance-none rounded-lg px-3 text-sm text-gray-900 placeholder-gray-400 ' +
                    styles.input
                  }
                  placeholder='One-Time Password'
                  value={otp}
                  onChange={(e) => {
                    setOtp(e.target.value);
                  }}
                  autoFocus={showOtp}
                />

                <input
                  id='password'
                  name='password'
                  type='password'
                  autoComplete='new-password'
                  disabled={resetMutation.isPending || success}
                  required
                  className={
                    'relative block h-10 w-full appearance-none rounded-lg px-3 text-sm text-gray-900 placeholder-gray-400 ' +
                    styles.input
                  }
                  placeholder='New Password'
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                  }}
                />
              </>
            )}

            <div>
              <Button
                type='submit'
                variant='primary'
                className='w-full'
                onClick={(e) => {
                  e.preventDefault();
                  void resetMutation.mutateAsync();
                }}
                disabled={
                  resetMutation.isPending ||
                  success ||
                  (showOtp && (otp.length < 5 || newPassword.length < 8))
                }
              >
                {resetMutation.isPending ? (
                  <BananaSpinner />
                ) : (
                  <>{showOtp ? 'Reset Password' : 'Get Reset Code'}</>
                )}
              </Button>

              {resetMutation.error && (
                <div className='mt-3 rounded-xl bg-red-100 px-4 py-3'>
                  <p className='text-center text-sm font-medium text-black'>
                    {getErrorMessage(resetMutation.error)}
                  </p>
                </div>
              )}

              {success && (
                <div className='mt-3 rounded-xl bg-green-100 px-4 py-3'>
                  <p className='text-center text-sm font-medium text-black'>
                    Password sucessfully reset!{' '}
                    <Link
                      to='/auth/signin'
                      className='font-semibold text-gray-900 hover:underline'
                    >
                      Back to login
                    </Link>
                  </p>
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
      <div className='hidden md:block md:w-1/2'>
        <RightSideImage />
      </div>
    </div>
  );
}
